import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { NAV_RESET_STATE } from '../../contexts/NavigationHistoryContext';
import { useUser } from '../../contexts/UserContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useSubscription } from '../../hooks/useSubscription';
import { useBatchCart } from '../../contexts/BatchCartContext';
import { cn } from '../../lib/utils';
import { ChevronDown, ClipboardList, Menu, X } from 'lucide-react';
import { TeamSwitcher } from './TeamSwitcher';
import UserMenu from './UserMenu';
import OmniSearchBar from '../Common/OmniSearchBar';
import NavigationBreadcrumbs from '../Common/NavigationBreadcrumbs';
import { StatusLabel } from '../Common';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { signOut } = useAuth();
  const { userAccount } = useUser();
  const {
    isStaff,
    canViewAdminUsers,
    canViewAdminTeams,
    canViewAdminRoleEntitlements,
    canViewAdminPlanEntitlements,
    canViewAdminAgentCoverage,
    canViewAdminAudit,
    canViewUnmatchedHoldings,
    canViewReviewDashboard,
    canViewReviewTasks,
    canViewReviewJobs,
    canViewReviewPriorities,
    canReadAiCosts,
    canViewCIKMatching,
    canViewFundManagement,
    canViewPromptConfig,
    canViewPortfolios,
    canViewIssuers,
    canViewSecurities,
    canViewScreens,
    canViewCharacteristics,
    canViewClaims,
    canViewEvidence,
    canViewProxyVoting,
    canCreateDef14aImport,
    canCreateNpxImport,
    canViewAssetCatMapping,
    canCreateBatchReview
  } = useEntitlements();
  const { subscription } = useSubscription();
  const { itemCount: batchCartCount, setIsOpen: setBatchCartOpen } = useBatchCart();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navSearchRef = useRef<HTMLInputElement | null>(null);

  const displayName = userAccount?.display_name;

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Cmd+K / Ctrl+K global shortcut to focus navbar search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navSearchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Hide compact navbar search on dashboard (dashboard has its own hero search)
  const isDashboard = location.pathname === '/dashboard';

  // Build Research dropdown
  const researchItems = [
    canViewPortfolios && { path: '/portfolios', label: 'Portfolios' },
    canViewIssuers && { path: '/issuers', label: 'Issuers' },
    canViewSecurities && { path: '/securities', label: 'Securities' },
    canViewScreens && { path: '/screens', label: 'Screens', divider: true },
    canViewCharacteristics && { path: '/characteristics', label: 'Characteristics' },
    canViewClaims && { path: '/claims', label: 'Claims' },
    canViewEvidence && { path: '/evidence', label: 'Evidence' },
    canViewProxyVoting && { path: '/proxy-proposals', label: 'Proposals', divider: true },
    canViewProxyVoting && { path: '/proxy-proposals/compare', label: 'Fund Vote Comparison' },
    canViewProxyVoting && { path: '/proxy-proposals/def14a', label: 'DEF 14A Filings' },
  ].filter(Boolean) as { path: string; label: string; divider?: boolean }[];

  // Build Reviews dropdown
  const reviewsItems = [
    canViewReviewDashboard && { path: '/reviews-dashboard', label: 'Dashboard' },
    canViewReviewTasks && { path: '/reviews-dashboard/tasks', label: 'Task Queue' },
    canViewReviewJobs && { path: '/reviews-dashboard/jobs', label: 'Jobs' },
    canViewReviewPriorities && { path: '/reviews-dashboard/priorities', label: 'Priorities' },
    canReadAiCosts && { path: '/reviews-dashboard/costs', label: 'Costs' },
  ].filter(Boolean) as { path: string; label: string }[];

  // Check if user has access to any utility
  const canViewUtilities = canViewFundManagement || canViewCIKMatching || canCreateDef14aImport ||
                           canCreateNpxImport || canViewPromptConfig || canViewAssetCatMapping ||
                           canViewUnmatchedHoldings || canViewAdminAudit || isStaff;

  // Build Admin dropdown
  const adminItems = [
    canViewAdminUsers && { path: '/admin/users', label: 'Users' },
    canViewAdminTeams && { path: '/admin/teams', label: 'Teams & Plans' },
    canViewAdminRoleEntitlements && { path: '/admin/role-entitlements', label: 'Role Entitlements' },
    canViewAdminPlanEntitlements && { path: '/admin/plan-entitlements', label: 'Plan Entitlements' },
    canViewAdminAgentCoverage && { path: '/admin/agent-coverage', label: 'Agent Coverage' },
    canViewUtilities && { path: '/utilities', label: 'Utilities', divider: true },
  ].filter(Boolean) as { path: string; label: string; divider?: boolean }[];

  const dropdowns: Record<string, { path: string; label: string; divider?: boolean }[]> = {};
  if (researchItems.length > 0) dropdowns['Research'] = researchItems;
  if (reviewsItems.length > 0) dropdowns['Reviews'] = reviewsItems;
  if (adminItems.length > 0) dropdowns['Admin'] = adminItems;

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <nav className={cn(
      "bg-background border-b border-border z-50",
      mobileMenuOpen
        ? "fixed inset-0 flex flex-col"
        : "sticky top-0"
    )}>
      <div className="nav flex-shrink-0">
        {/* Logo and Desktop Navigation */}
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/dashboard"
            state={NAV_RESET_STATE}
            className="no-underline flex items-center flex-shrink-0 transition-opacity hover:opacity-80"
            style={{ height: 30 }}
          >
            <img
              src="/iqa-acronym.png"
              alt="IQA"
              style={{ height: 30, width: 'auto', display: 'block', marginRight: -3 }}
            />
            <img
              src="/f-hole-integral.png"
              alt=""
              style={{ height: 40, width: 'auto', display: 'block', marginLeft: 8, marginRight: 10 }}
            />
            <span
              className="font-serif text-ink-900"
              style={{ fontSize: 18, fontWeight: 500, letterSpacing: '-0.005em', lineHeight: 1, whiteSpace: 'nowrap' }}
            >
              Integrity
            </span>
          </Link>

          {/* Desktop Menu - Dropdowns */}
          <div className="hidden md:flex gap-1 items-center">
            {Object.entries(dropdowns).map(([dropdownLabel, items]) => {
              const isActive = items.some(item => location.pathname === item.path);
              const isOpen = openDropdown === dropdownLabel;

              return (
                <div
                  key={dropdownLabel}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(dropdownLabel)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <div
                    className={cn(
                      'flex items-center gap-1 px-3 py-2 rounded cursor-pointer select-none font-sans transition-colors',
                      isActive
                        ? 'text-navy-700 bg-paper-2 font-medium'
                        : 'text-ink-700 hover:text-navy-700 hover:bg-paper-2'
                    )}
                    style={{ fontSize: 13.5 }}
                  >
                    {dropdownLabel}
                    <ChevronDown
                      size={14}
                      strokeWidth={1.25}
                      className={cn('text-ink-500 transition-transform duration-200', isOpen && 'rotate-180')}
                    />
                  </div>
                  {isOpen && (
                    <div className="absolute top-full left-0 pt-1 z-[1000]">
                      <div
                        className="bg-ivory min-w-[210px] rounded-lg py-1.5 animate-fade-in-up"
                        style={{
                          border: '1px solid var(--stone-300)',
                          boxShadow: '0 8px 24px rgba(26,31,42,0.10), 0 2px 6px rgba(26,31,42,0.06)',
                        }}
                      >
                        {items.map((item: { path: string; label: string; divider?: boolean }) => (
                          <React.Fragment key={item.path}>
                            {item.divider && (
                              <div className="border-t border-stone-200 my-1.5" />
                            )}
                            <Link
                              to={item.path}
                              state={NAV_RESET_STATE}
                              className={cn(
                                'block px-4 py-2 no-underline font-serif transition-colors',
                                location.pathname === item.path
                                  ? 'text-navy-700 bg-paper-2'
                                  : 'text-ink-900 hover:bg-paper-2'
                              )}
                              style={{ fontSize: 15 }}
                            >
                              {item.label}
                            </Link>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop Right Side: Search + Cart + UserMenu */}
        <div className="hidden md:flex items-center gap-3">
          {/* Compact OmniSearch (hidden on dashboard) */}
          {!isDashboard && (
            <OmniSearchBar variant="compact" inputRef={navSearchRef} />
          )}

          {/* Batch Review Queue Button */}
          {canCreateBatchReview && (
            <button
              onClick={() => setBatchCartOpen(true)}
              className="flex items-start p-2 rounded text-ink-700 hover:text-navy-700 hover:bg-paper-2 transition-colors"
              title="Batch Review Queue"
            >
              <ClipboardList size={20} strokeWidth={1.25} />
              {batchCartCount > 0 && (
                <span
                  className="font-mono text-navy-700 leading-none"
                  style={{ fontSize: 11.5, marginLeft: 1 }}
                >
                  {batchCartCount > 99 ? '99+' : batchCartCount}
                </span>
              )}
            </button>
          )}

          {/* User Menu (replaces TeamSwitcher + username + Sign Out) */}
          <UserMenu />
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-ink-700 hover:text-navy-700 hover:bg-paper-2 rounded transition-colors"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <X size={22} strokeWidth={1.25} />
          ) : (
            <Menu size={22} strokeWidth={1.25} />
          )}
        </button>
      </div>

      {/* Breadcrumbs - sticky with navbar, desktop only */}
      <NavigationBreadcrumbs />

      {/* Mobile Menu - full screen scrollable panel */}
      {mobileMenuOpen && (
        <div className="flex-1 overflow-y-auto overscroll-contain md:hidden border-t border-stone-200 bg-background">
          <div className="flex flex-col p-4 space-y-1 pb-20">
            {/* Mobile search */}
            <div className="pb-3">
              <OmniSearchBar variant="compact" className="[&_input]:w-full [&_input]:focus:w-full" />
            </div>

            {Object.entries(dropdowns).map(([dropdownLabel, items]) => (
              <div key={dropdownLabel} className="pt-2">
                <div className="px-4 py-2 eyebrow">
                  {dropdownLabel}
                </div>
                {items.map((item: { path: string; label: string; divider?: boolean }) => (
                  <React.Fragment key={item.path}>
                    {item.divider && (
                      <div className="border-t border-stone-200 mx-4 my-2" />
                    )}
                    <Link
                      to={item.path}
                      state={NAV_RESET_STATE}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        'block py-3 px-6 no-underline rounded-lg font-serif transition-colors',
                        location.pathname === item.path
                          ? 'text-navy-700 bg-paper-2 font-medium'
                          : 'text-ink-900 hover:bg-paper-2'
                      )}
                      style={{ fontSize: 15 }}
                    >
                      {item.label}
                    </Link>
                  </React.Fragment>
                ))}
              </div>
            ))}

            {/* Mobile user section */}
            <div className="border-t border-stone-200 mt-4 pt-4 px-4">
              <div className="font-sans text-ink-700 mb-1" style={{ fontSize: 12.5 }}>{displayName}</div>
              <TeamSwitcher className="mb-3" />
              <Link
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sm text-ink-500 hover:text-ink-900 mb-3 no-underline"
              >
                Profile & Settings
              </Link>
              {subscription && (subscription.billingHealth === 'payment_issue' || subscription.billingHealth === 'canceling') && (
                <Link
                  to="/profile?tab=subscription"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center mb-3 no-underline"
                >
                  <StatusLabel tone={subscription.billingHealth === 'payment_issue' ? 'critical' : 'caution'}>
                    {subscription.billingHealth === 'payment_issue' ? 'Payment issue' : 'Plan expires soon'}
                  </StatusLabel>
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="w-full btn-destructive"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
